import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect } from 'react';
import type { InterviewQuestion, InterviewSetup, SetupResponse } from '../models/interview';

interface InterviewState {
    interviewMode: string;
    interviewId: string | null;
    setup: InterviewSetup | null;
    questions: InterviewQuestion[];
    currentQuestionIndex: number;
    isCompleted: boolean;
}

interface InterviewContextType extends InterviewState {
    setInterviewMode: (mode: string) => void;
    startSetup: (data: SetupResponse & { setup?: InterviewSetup }) => void;
    setQuestionIndex: (index: number) => void;
    currentQuestion: InterviewQuestion | null;
    completeInterview: () => void;
    resetInterview: () => void;
    resetSession: () => void;
}

const InterviewContext = createContext<InterviewContextType | undefined>(undefined);

const STORAGE_KEY = 'prepme_interview_state';

export function InterviewProvider({ children }: { children: ReactNode }) {
    // Initialize state from localStorage
    const [interviewMode, setInterviewModeVal] = useState<string>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).interviewMode : '';
    });
    const [interviewId, setInterviewId] = useState<string | null>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).interviewId : null;
    });
    const [setup, setSetup] = useState<InterviewSetup | null>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).setup : null;
    });
    const [questions, setQuestions] = useState<InterviewQuestion[]>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).questions : [];
    });
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).currentQuestionIndex : 0;
    });
    const [isCompleted, setIsCompleted] = useState(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored).isCompleted : false;
    });

    // Persist state changes
    useEffect(() => {
        const state: InterviewState = {
            interviewMode,
            interviewId,
            setup,
            questions,
            currentQuestionIndex,
            isCompleted
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }, [interviewMode, interviewId, setup, questions, currentQuestionIndex, isCompleted]);

    const setInterviewMode = (mode: string) => {
        setInterviewModeVal(mode);
    };

    const startSetup = (data: SetupResponse & { setup?: InterviewSetup }) => {
        setInterviewId(data.interview_id);
        if (data.setup) {
            setSetup(data.setup);
        }
        setQuestions(data.questions);
        setCurrentQuestionIndex(0);
        setIsCompleted(false);
    };

    const setQuestionIndex = (index: number) => {
        if (index >= 0 && index < questions.length) {
            setCurrentQuestionIndex(index);
        }
    };

    const completeInterview = () => {
        setIsCompleted(true);
    };

    const resetInterview = () => {
        setInterviewModeVal('');
        setInterviewId(null);
        setSetup(null);
        setQuestions([]);
        setCurrentQuestionIndex(0);
        setIsCompleted(false);
        localStorage.removeItem(STORAGE_KEY);
    };

    const resetSession = () => {
        setInterviewId(null);
        setSetup(null);
        setQuestions([]);
        setCurrentQuestionIndex(0);
        setIsCompleted(false);
        // We do NOT clear interviewMode here
        // Update localStorage but keep mode
        const currentState = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
        const newState = { ...currentState, interviewId: null, setup: null, questions: [], currentQuestionIndex: 0, isCompleted: false };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    };

    const currentQuestion = questions[currentQuestionIndex] || null;

    const value = {
        interviewMode,
        interviewId,
        setup,
        questions,
        currentQuestionIndex,
        isCompleted,
        setInterviewMode,
        startSetup,
        setQuestionIndex,
        currentQuestion,
        completeInterview,
        resetInterview,
        resetSession
    };

    return (
        <InterviewContext.Provider value={value}>
            {children}
        </InterviewContext.Provider>
    );
}

export function useInterview() {
    const context = useContext(InterviewContext);
    if (context === undefined) {
        throw new Error('useInterview must be used within an InterviewProvider');
    }
    return context;
}
